<template>
    <div v-if="orderCompleted || wasRevoked">
        <section v-if="includeButtonAndModal" class="section">
            <div class="container">
                <button type="button" class="btn btn-outline-danger btn-block" @click="openRevocationModalForm($event)">
                    <span v-html="$t('orderRevocation.callToAction')"></span>
                </button>
            </div>
        </section>

        <modal
            v-if="includeButtonAndModal"
            ref="revokeModalForm"
            id="revokeOrderForm"
            size="modal-lg"
            :title="$t('orderRevocation.revokeOrderModalTitle')"
            :centered="true"
            :scrollable="true"
            :show="revokeOrderModal.show"
            :close="revokeOrderModal.hide"
            :closeable="revokeOrderModal.closeable"
        >
            <form
                method="post"
                action=""
                enctype="multipart/form-data"
                class="order-revoke-form"
                @submit="onRevocationFormSubmit($event)"
            >
                <div class="modal-body">
                    <div class="row">
                        <div v-if="revokeOrderResponse.show && revokeOrderResponse.message.length > 0" class="col-12">
                            <div
                                class="alert"
                                :class="{
                                    'alert-success': revokeOrderResponse.success,
                                    'alert-danger': !revokeOrderResponse.success,
                                }"
                            >
                                <p class="mb-0">{{ response.message }}</p>
                            </div>
                        </div>

                        <div class="col-12">
                            <p class="text-muted" v-html="$t('orderRevocation.revokeOrderModalText')"></p>

                            <div class="row align-items-center mt-4">
                                <div class="col-2 text-center">
                                    <input
                                        type="checkbox"
                                        id="tos_accepted"
                                        name="confirmed"
                                        class="checkbox"
                                        :class="{ 'checkbox-error': revokeConfirmError }"
                                        ref="tosCheckbox"
                                        :value="true"
                                        :disabled="runningRequest"
                                        v-model="revokeConfirmed"
                                    />
                                </div>
                                <div class="col-10">
                                    <label
                                        for="tos_accepted"
                                        class="smaller mb-0"
                                        v-html="$t('orderRevocation.checkboxText')"
                                    ></label>
                                    <div v-if="revokeConfirmError" class="invalid-feedback d-block">
                                        <span class="d-block">{{ $t('orderRevocation.confirmationMissing') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        :disabled="runningRequest"
                        @click="onRevocationCancel($event)"
                    >
                        <span v-html="$t('generic.cancel')"></span>
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="runningRequest">
                        <i v-if="runningRequest" class="mdi mdi-loading mdi-spin"></i>
                        <span v-else v-html="$t('orderRevocation.executeNow')"></span>
                    </button>
                </div>
            </form>
        </modal>

        <modal
            v-if="wasRevoked"
            ref="revokeModal"
            id="revokeModal"
            size="modal-lg"
            :title="
                $t('orderRevocation.revokedModalTitle', {
                    id: this.order.id,
                })
            "
            :centered="true"
            :scrollable="true"
            :show="revokeModal.show"
            :close="revokeModal.hide"
            :closeable="revokeModal.closable"
            class="revoked-order-modal"
            @modal-visible="onRevokedModalVisible"
            @modal-close="
                revokeModal.show = false;
                revokeModal.hide = false;
            "
            @modal-invisible="revokeModal.show = true"
        >
            <div class="modal-body">
                <p class="text-muted" v-html="$t('orderRevocation.revokedModalText')"></p>

                <div v-if="hasInvoice" class="text-center mt-1">
                    <a
                        :href="invoiceDownloadUrl"
                        target="_blank"
                        class="btn btn-sm btn-primary"
                        style="font-size: 1rem"
                    >
                        <i class="mdi mdi-download mr-1"></i>
                        {{ $t('order.download-invoice') }}
                    </a>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        orderCompleted: {
            type: Boolean,
            required: true,
        },
        buttonVisible: {
            type: Boolean,
            required: true,
        },
        order: {
            type: Object,
            required: true,
        },
        revocationUrl: {
            type: String,
            required: true,
        },
        invoiceDownloadUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            revokeModal: {
                show: false,
                hide: false,
                closable: false,
            },
            revokeOrderModal: {
                show: false,
                hide: false,
                closable: true,
            },
            revokeOrderResponse: {
                show: false,
                success: false,
                message: '',
            },
            revokeConfirmed: false,
            revokeConfirmError: false,
            runningRequest: false,
        };
    },

    computed: {
        /**
         * Determine if the order was already revoked.
         *
         * @returns {boolean}
         */
        wasRevoked() {
            return !!(typeof this.order.revoked === 'boolean' && this.order.revoked);
        },

        /**
         * Determine if the button and the modal should be rendered into the HTML DOM.
         *
         * @returns {boolean}
         */
        includeButtonAndModal() {
            return this.buttonVisible && !this.wasRevoked;
        },

        /**
         * Determine if the order has a invoice which should be downloadable, still if the order was revoked.
         *
         * @returns {boolean}
         * */
        hasInvoice() {
            return (
                typeof this.order.invoice_wanted !== 'undefined' &&
                [true, 1, 'true', '1'].indexOf(this.order.invoice_wanted) !== -1
            );
        },
    },

    methods: {
        /**
         * Handle the callback for open the revocation form.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        openRevocationModalForm($event) {
            // Prevent the default event, to prevent any strange behaviour by executing the default
            // event of module.
            $event.preventDefault();

            // In the case that the modal shouldn't visible, we don't have to handle anything here.
            if (!this.includeButtonAndModal) {
                return;
            }

            // Update the properties to handle the display of the modal.
            this.revokeOrderModal.hide = false;
            this.revokeOrderModal.show = true;
        },

        /**
         * Handle the event callback for submitting the revocation form.
         *
         * @param {SubmitEvent|PointerEvent} $event
         *
         * @returns {*}
         */
        onRevocationFormSubmit($event) {
            // Prevent the default event of the element, in this specific case it should be the submit
            // of the form. Because we want to handle it in a different way.
            $event.preventDefault();

            // In the case the form shouldn't visible, we have to cancel the processing by a ajax request.
            // Otherwise, if an request is already ongoing, we have to prevent another submit too.
            if (!this.includeButtonAndModal || this.runningRequest) {
                return;
            }

            // If the revoke of the order wasn't confirmed, we have to flag the issue at
            // scroll the checkbox into the user main view.
            if (!this.revokeConfirmed) {
                this.revokeConfirmError = true;

                return;
            }

            // Prevent another submit and prepare the data to be submitted.
            this.runningRequest = true;
            this.revokeConfirmError = false;
            this.revokeOrderResponse.show = false;
            this.revokeOrderResponse.success = false;
            this.revokeOrderResponse.message = '';

            const formData = new FormData();
            formData.append('confirmed', this.revokeConfirmed.toString());

            // Finally, send the request to the expected controller.
            return axios
                .post(this.revocationUrl, formData)
                .then((response) => {
                    // In the case the request was failed for some reason, we have to apply
                    // the message to our response property to show the user more details why
                    // the request was failed.
                    this.revokeOrderResponse.success = response.data.success;
                    this.revokeOrderResponse.message = response.data.message || '';
                    this.revokeOrderResponse.show = true;

                    // If the request of revocation was failed, we should prevent an reload
                    // of the current window. Because the result won't be changed.
                    if (!response.data.success) {
                        this.runningRequest = false;

                        return;
                    }

                    // Reload the current window, to remove this modal and form structure and
                    // show the new modal to inform the user about the case, that the order was
                    // revoked.
                    setTimeout(() => {
                        window.location.href = location.href;
                    }, 2000);
                })
                .catch(() => {
                    this.runningRequest = false;
                });
        },

        /**
         * Handle the event callback for cancel the revocation of order.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        onRevocationCancel($event) {
            // Like the other event handles, we need to prevent the default event.
            $event.preventDefault();

            // In the case that the modal shouldn't visible or an request is going on,
            // we have to prevent the close of this modal. Or the modal isn't closeable.
            if (!this.includeButtonAndModal || this.runningRequest || !this.revokeOrderModal.closable) {
                return;
            }

            this.revokeOrderModal.show = false;
            this.revokeOrderModal.hide = true;
        },

        /**
         * Handle the callback of visible revoke modal.
         *
         * @returns {void}
         */
        onRevokedModalVisible() {
            // Read the required data from DOM.
            const orderContainer = document.querySelector('.order-container');
            const modalBackdrop = document.querySelector('.modal-backdrop.show');
            const topNav = document.querySelector('#topnav');
            const topNavHeight = topNav.getBoundingClientRect().height;

            // In the case the order container does not have the revoked-order class yet,
            // we have to add it, to apply some customized styles for this case.
            if (!orderContainer.classList.contains('revoked-order')) {
                orderContainer.classList.add('revoked-order');
            }

            // The backdrop does not have the class, to indicate a linking to the current
            // order view.
            if (!modalBackdrop.classList.contains('revoked-order-backdrop')) {
                modalBackdrop.classList.add('revoked-order-backdrop');
            }

            // Update the offset of the modal backdrop, to place it above the order container and
            // keep the navigation of the page alive.
            modalBackdrop.style.top = topNavHeight + 'px';
            document.querySelector('#revokeModal').style.top = topNavHeight + 'px';
            document.querySelector('#revokeModal .modal-dialog').style.marginTop = topNavHeight * -1 + 'px';
        },

        /**
         *
         * @param {Number} rem
         *
         * @returns {number}
         */
        convertRemToPixels(rem) {
            return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
        },

        /**
         * Handle the event callback of the window resize.
         *
         * @returns {void}
         */
        onResize() {
            // In the case that the order was already revoked, we have to update
            // the revoked order modal only.
            if (!this.wasRevoked) {
                return;
            }

            this.onRevokedModalVisible();
        },
    },

    beforeUnmount() {
        window.removeEventListener('resize', () => {
            this.onResize();
        });
    },

    beforeMount() {
        window.addEventListener('resize', () => {
            this.onResize();
        });
    },

    mounted() {
        // In the case the order was revoked, we have to show the revocation info modal.
        if (this.wasRevoked) {
            this.revokeModal.show = true;
            this.revokeModal.hide = false;
            this.revokeModal.closable = false;
        }
    },
};
</script>
<style lang="scss">
.revoked-order-modal {
    z-index: 999;
}
.revoked-order-backdrop {
    z-index: 998;
}
</style>
